import React, { useState, useRef, useEffect } from "react";
import { X, Send, Sparkles } from "lucide-react";
import { api } from "../services/api";

// Lightweight, safe markdown -> JSX (bold + bullets + line breaks).
function MarkdownLite({ text }) {
  const inline = (s, k) => {
    const parts = s.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
    return parts.map((p, i) =>
      p.startsWith("**") && p.endsWith("**")
        ? <strong key={`${k}-${i}`}>{p.slice(2, -2)}</strong>
        : <React.Fragment key={`${k}-${i}`}>{p}</React.Fragment>
    );
  };
  const lines = (text || "").split("\n");
  return (
    <>
      {lines.map((ln, i) => {
        const t = ln.trim();
        if (!t) return <div key={i} className="md-gap" />;
        const bullet = /^[-*]\s+/.test(t);
        const sub = /^\s{2,}[-*]\s+/.test(ln);
        if (bullet || sub)
          return <div key={i} className={`md-li ${sub ? "sub" : ""}`}><span className="md-dot">•</span><span>{inline(t.replace(/^[-*]\s+/, ""), i)}</span></div>;
        return <div key={i} className="md-p">{inline(t, i)}</div>;
      })}
    </>
  );
}

const SUGGESTIONS = {
  kajaria: [
    "What is the biggest fulfillment risk today and why?",
    "Which dealers are at risk of churn?",
    "Why is the West GVT situation critical?",
    "Where do we have excess inventory?",
  ],
  vans: [
    "What is the #1 decision to take today and why?",
    "Which situation is promo-driven rather than real demand?",
    "Which dealer should the stocker call first?",
    "Where is demand not converting?",
  ],
  igl: [
    "Why is Dwarka Sector-8 (Station 142) high risk?",
    "What changed at Station 142 in the last 30 days?",
    "Which assets have repeated failures and overdue maintenance?",
    "Which certificate patterns look anomalous?",
  ],
  dalmia: [
    "What is the #1 fulfillment risk today and why?",
    "Why is East EBITDA/ton falling?",
    "Which order is at risk of missing OTIF?",
    "How is the petcoke cost spike affecting margin?",
  ],
  iglrv: [
    "Why was UP16GH3456 flagged?",
    "Which vehicles need verification today?",
    "What changed for DL01CD5678?",
    "Which hydro-tests are expiring soon?",
  ],
};

export default function AskDotIQ({ open, onClose, brand = "kajaria" }) {
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(`sess-${Math.random().toString(36).slice(2)}`);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, loading]);

  const ask = async (text) => {
    const question = (text || q).trim();
    if (!question || loading) return;
    setMsgs((m) => [...m, { role: "user", text: question }]);
    setQ("");
    setLoading(true);
    try {
      const r = brand === "vans"
        ? await api.vansQuery({ question, session_id: sessionId.current })
        : brand === "igl"
        ? await api.iglQuery({ question, session_id: sessionId.current })
        : brand === "dalmia"
        ? await api.dalmiaQuery({ question, session_id: sessionId.current })
        : brand === "iglrv"
        ? await api.rvQuery({ question, session_id: sessionId.current })
        : await api.query({ question, session_id: sessionId.current });
      setMsgs((m) => [...m, { role: "ai", text: r.answer }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "ai", text: "Sorry, I could not answer that right now." }]);
    } finally { setLoading(false); }
  };

  const suggestions = SUGGESTIONS[brand] || SUGGESTIONS.kajaria;

  return (
    <>
      <div className={`ask-overlay ${open ? "show" : ""}`} onClick={onClose} />
      <aside className={`ask-panel ${open ? "open" : ""}`}>
        <div className="ask-head">
          <div className="ask-head-title"><Sparkles size={18} /> Ask DotIQ</div>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="ask-body">
          {msgs.length === 0 && (
            <div className="ask-empty">
              <p>Ask about demand, inventory, production, logistics or fulfillment. Grounded in DotIQ's live correlation state.</p>
              <div className="ask-suggests">
                {suggestions.map((s) => (
                  <button key={s} className="suggest" onClick={() => ask(s)}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`bubble ${m.role}`}>
              {m.role === "ai" ? <MarkdownLite text={m.text} /> : m.text}
            </div>
          ))}
          {loading && <div className="bubble ai loading"><span className="dot" /><span className="dot" /><span className="dot" /></div>}
          <div ref={endRef} />
        </div>
        <div className="ask-input">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask a question…"
            onKeyDown={(e) => e.key === "Enter" && ask()} />
          <button className="btn-primary" onClick={() => ask()} disabled={loading || !q.trim()}><Send size={16} /></button>
        </div>
      </aside>
    </>
  );
}
